import React, { useMemo } from 'react';

import IconMC from '../../../../../../components/IconMC';
import { CONFIGURABLE_FLOW_TOOL_IDS } from '../constants/configurableFlowToolIds';
import { editorHtmlToTokenPlainText } from '../utils/instructionDescriptionTokens';
import {
  parseToolTokenPart,
  resolveToolEntryFromToken,
  splitTextWithToolTokens
} from '../utils/toolTokenEditor';

const CLICKABLE_TOOL_IDS = new Set(CONFIGURABLE_FLOW_TOOL_IDS);

const DescriptionWithToolPills = ({
  description,
  tools = [],
  getToolDetails,
  t,
  onToolPillClick,
  clickableToolIds = CLICKABLE_TOOL_IDS,
  partKeyPrefix = 'desc'
}) => {
  const parts = useMemo(() => {
    const text = editorHtmlToTokenPlainText(description || '');
    if (!text) return [];
    return splitTextWithToolTokens(text);
  }, [description]);

  if (parts.length === 0) return null;

  return parts.map((part, index) => {
    const token = parseToolTokenPart(part);
    const partKey = `${partKeyPrefix}-${index}-${part.slice(0, 16)}`;

    if (!token) {
      return <span key={partKey}>{part}</span>;
    }

    const { toolName, toolIndex } = token;
    const toolEntry = resolveToolEntryFromToken(tools, toolIndex, toolName);
    const toolDetails = getToolDetails(toolEntry.name);
    const isClickable = Boolean(onToolPillClick) && clickableToolIds.has(toolEntry.name);
    const pillClassName = `tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-px-2 tw-py-[2px] tw-mx-1 tw-align-middle ${
      isClickable ? 'tw-cursor-pointer hover:tw-bg-gray-100 tw-transition-colors' : ''
    }`;

    const handlePillActivate = (e) => {
      if (!isClickable) return;
      e.preventDefault();
      e.stopPropagation();
      onToolPillClick({
        toolId: toolEntry.name,
        toolIndex,
        args: toolEntry.args?.length ? toolEntry.args : null
      });
    };

    if (toolDetails) {
      return (
        <span
          key={partKey}
          className={pillClassName}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onClick={handlePillActivate}
          onKeyDown={(e) => {
            if (!isClickable) return;
            if (e.key === 'Enter' || e.key === ' ') handlePillActivate(e);
          }}
        >
          <div className={`tw-p-[2px] tw-rounded-md ${toolDetails.bgClass}`}>
            <IconMC name={toolDetails.icon} className={`${toolDetails.colorClass} !tw-text-[10px]`} size="sm" />
          </div>
          <span className="tw-text-2xs tw-text-gray-500">{t(toolDetails.nameKey)}</span>
        </span>
      );
    }

    return (
      <span
        key={partKey}
        className={pillClassName}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={handlePillActivate}
        onKeyDown={(e) => {
          if (!isClickable) return;
          if (e.key === 'Enter' || e.key === ' ') handlePillActivate(e);
        }}
      >
        <span className="tw-text-2xs tw-text-gray-500">{toolEntry.name}</span>
      </span>
    );
  });
};

export default DescriptionWithToolPills;
